import { FunctionComponent, useMemo, useState } from "react";

import Carousel from "components/common/ui/Carousel/Carousel";
import MoreColors from "components/MoreColours/MoreColours";
import PdpBasicInfo from "components/pdp/PdpDetails/PdpBasicInfo/PdpBasicInfo";
import PdpDescription from "components/pdp/PdpDetails/PdpDescription/PdpDescription";
import PdpActions from "./PdpActions/PdpActions";
import PdpSizeActions from "./PdpSizeActions/PdpSizeActions";

import SisterColourSKUs from "components/ProductSisterSKUs/SisterColourSKUs";

import { StyledColumn } from "pages/pdp/ProductDetailPage.Styled";

import {
	BelowDetailsDivider,
	StyledProductDetailsDivider,
	StyledProductDetailsWrapper,
	StyledProductVariants,
	StyledSectionWrapper,
	StyledMoreColorsTextNoTitleMode,
} from "./PdpDetails.Styled";

import {
	getCustomFieldsToRender,
	getSisterColourSKUs,
	getGroupId,
	getCroatiaMiddlePrice,
} from "app/utils/bigcommerceProductUtil";
import Skeleton from "components/common/ui/Skeleton/Skeleton";
import { Product, ProductCustomField, ProductVariant } from "ts/types";
import NotificationModal from "components/NotificationModal/NotificationModal";
import { useTranslation } from "react-i18next";

interface PdpMainSectionProps {
	loading?: boolean;
	product?: Product;
}

const PdpDetailsSection: FunctionComponent<PdpMainSectionProps> = ({
	loading = false,
	product,
}) => {
	const { t } = useTranslation();
	const [selectedVariant, setSelectedVariant] = useState<ProductVariant>();

	const handleVariantChange = (variant: ProductVariant) => {
		setSelectedVariant(variant);
	};

	const customFieldsToRender = useMemo(() => {
		if (!product) {
			return [] as ProductCustomField[];
		}

		return getCustomFieldsToRender(product);
	}, [product]);

	const groupId = useMemo(() => {
		if (!product) {
			return;
		}

		return getGroupId(product as Product);
	}, [product]);

	const sisterSkusCustomField = useMemo(() => {
		if (!product) {
			return;
		}

		return getSisterColourSKUs(product);
	}, [product]);

	const croatiaMiddlePrice = useMemo(() => {
		if (!product) {
			return;
		}

		return getCroatiaMiddlePrice(product);
	}, [product]);

	// Define groups of ids for which SisterSku will be displayed directly with product images (without modal).
	const groupListDisplayNewModeSisterSku = useMemo(() => {return [200, 201, 202, 203]},[]);

	// We determine if the product is part of the id groups for which modal-free display is desired for SisterSku.
	const isNewMoreColorsMode = useMemo(() => {
		let displayNewMode = false;
		if(groupId?.value){
			displayNewMode = groupListDisplayNewModeSisterSku.some(item => item === parseInt(groupId?.value));
		}
		return displayNewMode;
	},[groupId, groupListDisplayNewModeSisterSku]);

	if (!loading && !product) {
		return null;
	}

	return (
		<StyledSectionWrapper className="PdpDetails">
			<Carousel loading={loading} images={product?.images || []} highRes />

			<StyledProductDetailsWrapper>
				<StyledColumn>
					<PdpBasicInfo
						loading={loading}
						name={product?.name || ""}
						basePrice={product?.basePrice}
						price={product?.price}
						salePrice={product?.salePrice}
						croatiaMiddlePrice={croatiaMiddlePrice?.value}
					/>
					{loading && (
						<Skeleton style={{ marginTop: "1rem" }} width={80} height={10} />
					)}
					{!loading && sisterSkusCustomField && ( 
						isNewMoreColorsMode ? 
						<>
							<StyledMoreColorsTextNoTitleMode>{t("more_colors")}</StyledMoreColorsTextNoTitleMode>
							<SisterColourSKUs field={sisterSkusCustomField} displayNoTitle={true} /> 
						</> :
						<MoreColors field={sisterSkusCustomField} />
					)}
				</StyledColumn>

				<StyledProductDetailsDivider />

				<StyledColumn>
					<PdpSizeActions loading={loading} product={product} />
				</StyledColumn>
				<StyledColumn>
					<StyledProductVariants
						loading={loading}
						variants={product?.variants || []}
						defaultValue={selectedVariant}
						onChange={handleVariantChange}
						outOfStockSelectable={true}
						controlled
						groupId={groupId?.value}
					/>
				</StyledColumn>
				<StyledColumn>
					<PdpActions
						loading={loading}
						onAddToCartSuccess={() => setSelectedVariant(undefined)}
						selectedVariant={selectedVariant}
						product={product}
					/>
				</StyledColumn>
				<StyledColumn>
					<PdpDescription
						loading={loading}
						category={product?.categories?.name || ""}
						customFields={customFieldsToRender || ""}
						description={product?.description || ""}
						sku={
							product
								? !selectedVariant
									? product.sku
									: selectedVariant.sku
								: ""
						}
					/>
				</StyledColumn>
			</StyledProductDetailsWrapper>
		</StyledSectionWrapper>
	);
};

export default PdpDetailsSection;
